import React, { lazy, Suspense, useEffect } from 'react';

import { TrackingContextProvider, useTrackingContext } from '@context/TrackingContext';
import { EcommerceSectionNamespace, trackServiceInfoModalOpen } from '@services/ecommerceService';
import { ModalErrorBoundary } from '@services/errorService';
import { Loader } from '@sharedComponents/Loader';

import { SERVICES_INFO_MODAL_SIZE } from '../components';
import { ECartPageState, useCartPageContext } from '../context';

import EEcommerceName = EcommerceSectionNamespace.EEcommerceName;

const ServicesInfoModal = lazy(() => import('../components/ServicesInfoModal/ServicesInfoModal'));

export const useServicesInfoModal = (onModalClose: () => void): JSX.Element => {
  const {
    state: {
      cartPageState,
      serviceItems: { insurance, giftPackagings },
    },
    actions: { toggleInsurance, setCartPageState },
    settings: { insuranceLinkUrl, giftPackagingLinkUrl },
  } = useCartPageContext();

  const { getTimeFromInit } = useTrackingContext();
  const lastPageViewMs = React.useRef(0);
  TrackingContextProvider.useSubscribeToInitTimeReset(() => {
    lastPageViewMs.current = 0;
  });

  const eecommerceServiceName = (pageState: ECartPageState) => {
    switch (pageState) {
      case ECartPageState.INSURANCE_SERVICE_INFO_MODAL:
        return EEcommerceName.INSURANCE_INFO;
      case ECartPageState.GIFT_PACKAGING_SERVICE_INFO_MODAL:
        return EEcommerceName.GIFT_PACKAGING_INFO;
      default:
        return EEcommerceName.INSURANCE_INFO;
    }
  };

  useEffect(() => {
    if (
      cartPageState ===
      (ECartPageState.INSURANCE_SERVICE_INFO_MODAL || ECartPageState.GIFT_PACKAGING_SERVICE_INFO_MODAL)
    ) {
      trackServiceInfoModalOpen(getTimeFromInit() - lastPageViewMs.current, eecommerceServiceName(cartPageState));
    }
  }, [cartPageState, getTimeFromInit]);

  return (
    <ModalErrorBoundary onError={onModalClose}>
      <Suspense fallback={<Loader height={SERVICES_INFO_MODAL_SIZE} />} />
      <ServicesInfoModal
        cartState={cartPageState}
        setCartPageState={setCartPageState}
        toggleInsurance={toggleInsurance}
        insurance={insurance}
        giftPackagingBoxes={giftPackagings}
        onModalClose={onModalClose}
        insuranceLinkUrl={insuranceLinkUrl}
        giftPackagingLinkUrl={giftPackagingLinkUrl}
      />
    </ModalErrorBoundary>
  );
};
